import { useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Download } from 'lucide-react';

// Each category is exported as its own block: a statistics header, then its items.
// Both header rows share a column count so the blocks line up in a spreadsheet.
const CSV_CATEGORY_COLUMNS = [
  'Category',
  'Variants',
  'Quantity Sold',
  'Revenue',
  '% of Total Revenue',
];

const CSV_ITEM_COLUMNS = [
  'Item Name',
  'Quantity Sold',
  'Avg Price',
  'Revenue',
  '% of Category Revenue',
];

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).replace(/"/g, '""');
  return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
};

export default function SalesByCategoryList({ categories = [] }) {
  const formatCurrency = useCallback((amount) => {
    const normalized = Object.is(amount, -0) ? 0 : amount || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  }, []);

  const formatNumber = useCallback(
    (value) => new Intl.NumberFormat('en-US').format(value || 0),
    []
  );

  const formatPercent = useCallback(
    (value) => `${(value || 0).toFixed(2)}%`,
    []
  );

  const handleExportCSV = useCallback(() => {
    if (!categories || categories.length === 0) return;

    const toRow = (values) => values.map(escapeCsvValue).join(',');
    const blankRow = toRow(new Array(CSV_CATEGORY_COLUMNS.length).fill(''));

    const rows = categories.flatMap((category, index) => [
      ...(index > 0 ? [blankRow] : []),
      toRow(CSV_CATEGORY_COLUMNS),
      toRow([
        category.category,
        category.variantCount,
        category.totalQuantity,
        category.totalRevenue,
        category.percentOfTotalRevenue,
      ]),
      toRow(CSV_ITEM_COLUMNS),
      ...(category.items || []).map((item) =>
        toRow([
          item.itemName,
          item.totalQuantity,
          item.avgPrice,
          item.totalRevenue,
          item.percentOfCategoryRevenue,
        ])
      ),
    ]);

    const csv = rows.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `sales-by-category-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [categories]);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Revenue by category
          </h2>
          <p className="text-sm text-muted-foreground">
            Tap any category to see its items.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="no-print"
          onClick={handleExportCSV}
          disabled={!categories || categories.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          No categories found
        </Card>
      ) : (
        <Accordion type="multiple" className="space-y-4">
          {categories.map((category) => (
            <AccordionItem
              key={category.category}
              value={category.category}
              className="border-none"
            >
              <Card>
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-semibold truncate">
                        {category.category}
                      </span>
                      <span className="font-semibold shrink-0">
                        {formatCurrency(category.totalRevenue)}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {formatNumber(category.variantCount)}{' '}
                      {category.variantCount === 1 ? 'variant' : 'variants'} ·{' '}
                      {formatNumber(category.totalQuantity)} sold
                    </div>
                    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-green-500"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, category.percentOfTotalRevenue || 0)
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {formatPercent(category.percentOfTotalRevenue)} of total
                      revenue
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-6">
                  <div className="flex gap-3">
                    <div className="flex-1 rounded-lg border bg-muted p-3">
                      <div className="text-xs font-medium text-muted-foreground">
                        REVENUE
                      </div>
                      <div className="mt-1 font-semibold">
                        {formatCurrency(category.totalRevenue)}
                      </div>
                    </div>
                    <div className="flex-1 rounded-lg border bg-muted p-3">
                      <div className="text-xs font-medium text-muted-foreground">
                        ITEMS SOLD
                      </div>
                      <div className="mt-1 font-semibold">
                        {formatNumber(category.totalQuantity)}
                      </div>
                    </div>
                    <div className="flex-1 rounded-lg border bg-muted p-3">
                      <div className="text-xs font-medium text-muted-foreground">
                        % OF TOTAL
                      </div>
                      <div className="mt-1 font-semibold">
                        {formatPercent(category.percentOfTotalRevenue)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      ITEMS IN THIS CATEGORY
                    </div>
                    {(category.items || []).map((item, index) => (
                      <div
                        key={`${item.itemId || item.itemName}-${index}`}
                        className="flex items-start justify-between gap-4 border-t py-2"
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {item.itemName}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {formatNumber(item.totalQuantity)} sold ·{' '}
                            {formatCurrency(item.avgPrice)}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-medium">
                            {formatCurrency(item.totalRevenue)}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {formatPercent(item.percentOfCategoryRevenue)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </Card>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
